/**
 * mechatReadState.ts — which incoming messages have already been seen.
 *
 * Per account we keep, for every peer, the timestamp of the newest incoming
 * message the user has actually opened. Anything newer than that is unread.
 *
 * Timestamps are stored exactly as Hyperion returns them (e.g.
 * `2026-08-16T12:22:57.500`), so plain string comparison is a valid ordering —
 * do not mix in `Date.toISOString()` values, which carry a trailing `Z`.
 */

const PREFIX = 'mechat.read.'

/** peer account -> timestamp of the newest message read in that conversation. */
export type ReadState = Record<string, string>

export interface UnreadLike {
  direction: 'in' | 'out'
  from: string
  timestamp: string
}

function keyFor(account: string): string {
  return `${PREFIX}${account}`
}

export function loadReadState(account: string): ReadState {
  if (!account || typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(keyFor(account))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const out: ReadState = {}
    for (const [peer, ts] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof ts === 'string') out[peer] = ts
    }
    return out
  } catch {
    return {}
  }
}

function save(account: string, state: ReadState): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(keyFor(account), JSON.stringify(state))
  } catch {
    /* ignore quota */
  }
}

export function hasReadState(account: string): boolean {
  if (!account || typeof localStorage === 'undefined') return false
  return localStorage.getItem(keyFor(account)) !== null
}

/** Marks everything currently on chain as read. */
export function markAllRead(account: string, messages: UnreadLike[]): ReadState {
  const state: ReadState = { ...loadReadState(account) }
  for (const m of messages) {
    if (m.direction !== 'in') continue
    if (!state[m.from] || m.timestamp > state[m.from]) state[m.from] = m.timestamp
  }
  save(account, state)
  return state
}

/**
 * First run for an account: treat the existing history as already seen, so the
 * user is not greeted by "47 unread" from messages they read in PayCash years ago.
 */
export function ensureReadStateInitialized(account: string, messages: UnreadLike[]): ReadState {
  if (hasReadState(account)) return loadReadState(account)
  return markAllRead(account, messages)
}

export function markPeerRead(account: string, peer: string, messages: UnreadLike[]): ReadState {
  const newest = messages
    .filter(m => m.direction === 'in' && m.from === peer)
    .reduce<string>((max, m) => (m.timestamp > max ? m.timestamp : max), '')
  const state = { ...loadReadState(account) }
  if (newest && (!state[peer] || newest > state[peer])) {
    state[peer] = newest
    save(account, state)
  }
  return state
}

export function countUnread(
  messages: UnreadLike[],
  state: ReadState,
): { byPeer: Record<string, number>; total: number } {
  const byPeer: Record<string, number> = {}
  let total = 0
  for (const m of messages) {
    if (m.direction !== 'in') continue
    const lastRead = state[m.from]
    if (lastRead && m.timestamp <= lastRead) continue
    byPeer[m.from] = (byPeer[m.from] ?? 0) + 1
    total++
  }
  return { byPeer, total }
}
